<template>
  <div class="pager" ref="pagerEl" @scroll="loadMore" :class="reversed ? 'reversed' : ''">
    <slot></slot>
    <div class="loader-wrapper" v-if="loading">
      <div v-for="index in loaderCount" :key="index">
        <slot name="loader"></slot>
      </div>
    </div>
    <div v-else class="wrapper-string__empty">
      <div v-if="emptyString && !errorLoad" class="string__empty">{{ emptyString }}</div>
      <div v-else-if="errorLoad" class="string__empty">{{ $t('ui.pager_try_again_later') }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';

const emit = defineEmits(['more', 'nomore',]);

const props = defineProps({
  count: Number,
  errorLoad: {
    type: Boolean,
    default: false,
  },
  reversed: {
    type: Boolean,
    default: false,
  },
  externalNextKey: {
    type: Number,
    default: 0,
  },
  dontLoadAtStart: {
    type: Boolean,
    default: false,
  },
  pageLoadRequest: Function,
  category: String,
  loaderCount: {
    type: Number,
    default: 1,
  },
  order: {
    type: String,
    default: 'DESC',
  },
  emptyString: {
    type: String,
  },
});

const offset = ref(0);
const called = ref(0);
const loading = ref(false);

const pagerEl = ref(null);

const loadElements = () => {
  if (!props.pageLoadRequest) {
    return;
  }
  loading.value = true;
  called.value += 1;
  props.pageLoadRequest(
    props.category,
    offset.value,
    props.count,
    called.value,
    props.order,
    (data) => {
      offset.value = data && data.offset;
      loading.value = false;
      if (offset.value) {
        emit('more');
      } else {
        emit('nomore');
      }
    }
  );
};

const reload = () => {
  offset.value = 0;
  called.value = 0;
  loadElements();
};

defineExpose({
  reload,
}); 

const loadMore = (ev) => {
  const pager = pagerEl.value;
  if (!props.reversed && pager.scrollTop < (pager.scrollHeight) * 0.8) {
    return;
  }
  
  console.log('[PAGER] loadMore called', {
    reversed: props.reversed,
    scrollTop: pager.scrollTop,
    scrollHeight: pager.scrollHeight,
    clientHeight: pager.clientHeight,
  });

  if (props.reversed) {
    const actualTop = pager.clientHeight - pager.scrollHeight;
    console.log('[PAGER reversed] actualTop', actualTop, 'scrollTop', ev.target.scrollTop);
    if (Math.abs(actualTop - ev.target.scrollTop) > 100) {
      // far away from top, do not load more
      return;
    }

    if (Math.abs(actualTop - ev.target.scrollTop) <= 5) {
      // absolute equal is also bad, it happens e.g. on mount when during resuze it fires @scroll - dif is 0.111
      // so we will ignore
      return;
    }
    // 1 - 100 px left to top - load it now
  }
  console.log('[PAGER] loadMore check passed', {
    offset: offset.value,
    externalNextKey: props.externalNextKey,
    loading: loading.value,
    pageLoadRequest: props.pageLoadRequest,
  });
  if ((offset.value || props.externalNextKey) && !loading.value && props.pageLoadRequest) {
    loadElements();
  }
};

watch(() => props.reversed, reload);

onMounted(() => {
  if (!props.dontLoadAtStart) {
    loadElements();
  }
});

</script>

<style lang="scss" scoped>
$placeholder: var(--text);
.pager {
  height: 100%;
  display: flex;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  flex-direction: column;
  .load-wrapper {
    align-self: center;
    height: 106px;
    width: 16px;
    margin-top: 0;
    margin-bottom: 18px;
    position: relative;
  }
  :deep(.loader[class]) {
    transform: scale(0.3);
    margin-bottom: 0;
    margin-top: -18px;
    position: absolute;
  }
}
.reversed {
  display: flex;
  flex-direction: column-reverse;
}

.wrapper-string__empty {
  margin: auto;
  width: 100%;
  text-align: center;
}

.string__empty {
  font-size: 18px;
  color: var(--text);
  margin: auto;
}

.imgWrapper-preloader {
  min-width: 124px;
  min-height: 82px;
  max-width: 124px;
  max-height: 82px;
  margin: 12px;
  cursor: pointer;
  box-shadow: 1px 1px 6px var(--shadow-color);
  overflow: hidden;
  border-radius: 2px;
  opacity: 0.3;
  background: linear-gradient(-35deg, var(--skeleton-sheen), var(--skeleton), var(--skeleton-sheen));
  background-size: 800% 800%;
  animation: gradient 2s ease infinite;
  position: relative;
}
@keyframes gradient {
  100% {
    background-position: 0% 50%;
  }
  0% {
    background-position: 100% 50%;
  }
}

@media screen and (max-width: 600px) {
  .imgWrapper-preloader {
    min-width: 130px;
    min-height: 91px;
    max-width: 130px;
    max-height: 91px;
    margin: 8px;
  }
}

@media screen and (min-width: 600px) and (max-width: 1200px) {
  .imgWrapper-preloader {
    min-width: 116px;
    min-height: 91px;
    max-width: 116px;
    max-height: 91px;
    margin: 5px;
  }
}
</style>
